import React, { useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Button, Copy01, DotsGrid, Folder, LayersThree01, Plus, Trash01, XClose } from '@opendate-app/setlist';

import './customWidgetBuilder.css';
import AddItemMenu from './AddItemMenu';
import DestructiveConfirmModal from '../../../../shared/DestructiveConfirmModal';
import { toastError } from '../../../../shared/toastHelper';
import {
  createCheckoutCategory,
  updateCheckoutCategory,
  duplicateCheckoutCategory,
  deleteCheckoutCategory,
  updateCheckoutCategoryPosition,
  createCheckoutGroup,
  updateCheckoutGroup,
  deleteCheckoutGroup,
  updateCheckoutGroupPosition,
  moveCheckoutGroup,
  createCheckoutGroupItem,
  deleteCheckoutGroupItem,
  updateCheckoutGroupItemPosition,
  moveCheckoutGroupItem,
  canAddDonation,
  canAddSeatedAdmissionChart,
  canAddSeatedAddOnChart
} from '../../utils/checkoutStructure';

const ITEM_TYPE_KEY_TO_DOT_CLASS = {
  TicketType:        'ticket',
  AddOn:             'addon',
  SeatingChartEvent: 'seatmap',
  Donation:          'donation',
  MembershipProduct: 'membership'
};

const SELF_MANAGING_SUFFIX =
  'Opendate will stop placing new ticket types, add-ons and memberships into this ' +
  "event's checkout for you — you will arrange them yourself from here on. Continue?";

const SELF_MANAGING_MESSAGES = {
  addTab:       `Adding your own tab means ${SELF_MANAGING_SUFFIX}`,
  duplicateTab: `Duplicating a tab means ${SELF_MANAGING_SUFFIX}`,
  renameTab:    `Renaming this tab means ${SELF_MANAGING_SUFFIX}`,
  addGroup:     `Adding your own group means ${SELF_MANAGING_SUFFIX}`
};

const LEGEND_ITEMS = [
  { dotClass: 'ticket',     label: 'Ticket' },
  { dotClass: 'addon',      label: 'Add-on' },
  { dotClass: 'seatmap',    label: 'Seating Chart' },
  { dotClass: 'donation',   label: 'Donation' },
  { dotClass: 'membership', label: 'Membership' }
];

const itemKey = (item) => {
  if (item.enable_donation) return 'Donation';
  if (item.seatsio_event_id || item.add_on_seatsio_event_id) return 'SeatingChartEvent';
  return item.item_type;
};

const itemDotClass = (item) =>
  ITEM_TYPE_KEY_TO_DOT_CLASS[itemKey(item)] || 'ticket';

const itemDisplayName = (item) => {
  if (item.enable_donation) return 'Make a donation';
  if (item.seatsio_event_id) return 'Seated Admission Chart';
  if (item.add_on_seatsio_event_id) return 'Seated Add-on Chart';
  return item.item_name || '(unnamed)';
};

const itemPrice = (item) => {
  const price = item.item_price;
  if (price === null || price === undefined || price === '') return null;
  const num = Number(price);
  if (Number.isNaN(num)) return null;
  return `$${num % 1 === 0 ? num : num.toFixed(2)}`;
};

const parseTabId = (droppableId) => {
  const match = droppableId.match(/^tab-(\d+)-groups$/);
  return match ? Number(match[1]) : null;
};

const parseGroupId = (droppableId) => {
  const match = droppableId.match(/^group-(\d+)-items$/);
  return match ? Number(match[1]) : null;
};

const replaceCategory = (categories, id, mutator) =>
  categories.map((c) => (c.id === id ? mutator(c) : c));

const RenameableName = ({ value, onCommit }) => {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const commit = () => {
    const next = draft.trim();
    if (!next || next === value) {
      setDraft(value);
      return;
    }
    onCommit(next);
  };

  return (
    <input
      type="text"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.currentTarget.blur();
        } else if (e.key === 'Escape') {
          setDraft(value);
          e.currentTarget.blur();
        }
      }}
      onClick={(e) => e.stopPropagation()}
    />
  );
};

const StructureTab = ({
  csrfToken,
  team,
  confirm,
  checkoutCategories = [],
  ticketTypes = [],
  addOns = [],
  memberships = [],
  seatingChartEvents = [],
  onCheckoutCategoriesChange,
  confirmSelfManaging,
  reloadCheckoutCategories
}) => {
  const [pendingDelete, setPendingDelete] = useState(null);
  const [reordering, setReordering] = useState(false);

  const setCategories = (next) => {
    if (onCheckoutCategoriesChange) onCheckoutCategoriesChange(next);
  };

  const confirmLatching = (message) =>
    confirmSelfManaging ? confirmSelfManaging(message) : true;

  const failed = (message) => () => {
    toastError(message);
    if (reloadCheckoutCategories) reloadCheckoutCategories();
  };

  // === Tab actions ===

  const handleAddTab = () => {
    if (!confirmLatching(SELF_MANAGING_MESSAGES.addTab)) return;

    createCheckoutCategory({ team, csrfToken, confirm, name: 'New Tab' })
      .then(({ data }) => setCategories([...checkoutCategories, data]))
      .catch(failed('Failed to add tab'));
  };

  const handleDuplicateTab = (tab) => {
    if (!confirmLatching(SELF_MANAGING_MESSAGES.duplicateTab)) return;

    duplicateCheckoutCategory({ team, csrfToken, category: tab })
      .then(({ data }) => setCategories([...checkoutCategories, data]))
      .catch(failed('Failed to duplicate tab'));
  };

  const handleDeleteTab = (tab) => {
    if (checkoutCategories.length <= 1) return;
    setCategories(checkoutCategories.filter((c) => c.id !== tab.id));
    deleteCheckoutCategory({ team, csrfToken, category: tab })
      .catch(failed('Failed to delete tab'));
  };

  const handleRenameTab = (tab, name) => {
    if (name === tab.name) return;
    if (!confirmLatching(SELF_MANAGING_MESSAGES.renameTab)) return;

    setCategories(
      checkoutCategories.map((c) => (c.id === tab.id ? { ...c, name } : c))
    );
    updateCheckoutCategory({
      team,
      csrfToken,
      category: tab,
      attrs: { name }
    }).catch(failed('Failed to rename tab'));
  };

  const handleDeleteItem = (item) => {
    setCategories(
      checkoutCategories.map((c) => ({
        ...c,
        checkout_groups: (c.checkout_groups || []).map((g) => ({
          ...g,
          checkout_group_items: (g.checkout_group_items || []).filter((it) => it.id !== item.id)
        }))
      }))
    );
    deleteCheckoutGroupItem({ team, csrfToken, item })
      .catch(failed('Failed to remove item'));
  };

  const requestDeleteTab = (tab) => {
    if (checkoutCategories.length <= 1) return;

    const groups = tab.checkout_groups || [];
    const itemCount = groups.reduce(
      (sum, g) => sum + (g.checkout_group_items || []).length, 0
    );

    setPendingDelete({
      title: 'Delete this tab?',
      body: `"${tab.name}" and everything in it — ${groups.length} ${groups.length === 1 ? 'group' : 'groups'} and ${itemCount} ${itemCount === 1 ? 'item' : 'items'} — will be permanently removed.`,
      confirmLabel: 'Delete tab',
      onConfirm: () => handleDeleteTab(tab)
    });
  };

  const requestDeleteGroup = (tab, group) => {
    const itemCount = (group.checkout_group_items || []).length;

    setPendingDelete({
      title: 'Delete this group?',
      body: `"${group.name}" and the ${itemCount} ${itemCount === 1 ? 'item' : 'items'} in it will be permanently removed.`,
      confirmLabel: 'Delete group',
      onConfirm: () => handleDeleteGroup(tab.id, group)
    });
  };

  const requestDeleteItem = (item) => {
    setPendingDelete({
      title: 'Remove this item?',
      body: 'It will no longer appear in this checkout. You can add it back later.',
      confirmLabel: 'Remove item',
      onConfirm: () => handleDeleteItem(item)
    });
  };

  // === Group actions ===

  const handleAddGroup = (tab) => {
    if (!confirmLatching(SELF_MANAGING_MESSAGES.addGroup)) return;

    createCheckoutGroup({ team, csrfToken, category: tab })
      .then(({ data }) => {
        setCategories(
          replaceCategory(checkoutCategories, tab.id, (c) => ({
            ...c,
            checkout_groups: [...(c.checkout_groups || []), data]
          }))
        );
      })
      .catch(failed('Failed to add group'));
  };

  const handleDeleteGroup = (tabId, group) => {
    setCategories(
      replaceCategory(checkoutCategories, tabId, (c) => ({
        ...c,
        checkout_groups: (c.checkout_groups || []).filter((g) => g.id !== group.id)
      }))
    );
    deleteCheckoutGroup({ team, csrfToken, group })
      .catch(failed('Failed to delete group'));
  };

  const handleRenameGroup = (tabId, group, name) => {
    setCategories(
      replaceCategory(checkoutCategories, tabId, (c) => ({
        ...c,
        checkout_groups: (c.checkout_groups || []).map((g) =>
          g.id === group.id ? { ...g, name } : g
        )
      }))
    );
    updateCheckoutGroup({
      team,
      csrfToken,
      group,
      attrs: { name }
    }).catch(failed('Failed to rename group'));
  };

  const handleSelectAddItem = (groupId, payload) => {
    createCheckoutGroupItem({
      team,
      csrfToken,
      payload: { ...payload, checkout_group_id: groupId }
    })
      .then(({ data }) => {
        const updated = checkoutCategories.map((c) => ({
          ...c,
          checkout_groups: (c.checkout_groups || []).map((g) =>
            g.id === groupId
              ? { ...g, checkout_group_items: [...(g.checkout_group_items || []), data] }
              : g
          )
        }));
        setCategories(updated);
      })
      .catch(failed('Failed to add item'));
  };

  // === Drag-and-drop ===

  const handleDragEnd = (result) => {
    const { source, destination, type } = result;
    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    setReordering(true);

    if (type === 'tab') {
      const next = [...checkoutCategories];
      const [moved] = next.splice(source.index, 1);
      next.splice(destination.index, 0, moved);
      setCategories(next);
      updateCheckoutCategoryPosition({
        team,
        csrfToken,
        category: { ...moved, position: destination.index + 1 }
      }).catch(failed('Failed to reorder tab')).finally(() => setReordering(false));
      return;
    }

    if (type === 'group') {
      const sourceTabId = parseTabId(source.droppableId);
      const destTabId = parseTabId(destination.droppableId);
      const sourceTab = checkoutCategories.find((c) => c.id === sourceTabId);
      const destTab = checkoutCategories.find((c) => c.id === destTabId);
      if (!sourceTab || !destTab) return;

      const movedGroup = (sourceTab.checkout_groups || [])[source.index];
      if (!movedGroup) return;

      if (sourceTabId === destTabId) {
        const groups = [...(sourceTab.checkout_groups || [])];
        groups.splice(source.index, 1);
        groups.splice(destination.index, 0, movedGroup);
        setCategories(
          replaceCategory(checkoutCategories, sourceTabId, (c) => ({
            ...c,
            checkout_groups: groups
          }))
        );
        updateCheckoutGroupPosition({
          team,
          csrfToken,
          group: { ...movedGroup, position: destination.index + 1 }
        }).catch(failed('Failed to reorder group')).finally(() => setReordering(false));
      } else {
        const sourceGroups = [...(sourceTab.checkout_groups || [])];
        sourceGroups.splice(source.index, 1);
        const destGroups = [...(destTab.checkout_groups || [])];
        const moved = { ...movedGroup, checkout_category_id: destTabId };
        destGroups.splice(destination.index, 0, moved);

        setCategories(
          checkoutCategories.map((c) => {
            if (c.id === sourceTabId) return { ...c, checkout_groups: sourceGroups };
            if (c.id === destTabId) return { ...c, checkout_groups: destGroups };
            return c;
          })
        );
        moveCheckoutGroup({
          team,
          csrfToken,
          group: movedGroup,
          newCheckoutCategoryId: destTabId,
          position: destination.index + 1
        }).catch(failed('Failed to move group')).finally(() => setReordering(false));
      }
      return;
    }

    if (type === 'item') {
      const sourceGroupId = parseGroupId(source.droppableId);
      const destGroupId = parseGroupId(destination.droppableId);

      let sourceGroup = null;
      let destGroup = null;
      checkoutCategories.forEach((c) => {
        (c.checkout_groups || []).forEach((g) => {
          if (g.id === sourceGroupId) sourceGroup = g;
          if (g.id === destGroupId) destGroup = g;
        });
      });
      if (!sourceGroup || !destGroup) return;

      const movedItem = (sourceGroup.checkout_group_items || [])[source.index];
      if (!movedItem) return;

      if (sourceGroupId === destGroupId) {
        const items = [...(sourceGroup.checkout_group_items || [])];
        items.splice(source.index, 1);
        items.splice(destination.index, 0, movedItem);
        const updated = checkoutCategories.map((c) => ({
          ...c,
          checkout_groups: (c.checkout_groups || []).map((g) =>
            g.id === sourceGroupId ? { ...g, checkout_group_items: items } : g
          )
        }));
        setCategories(updated);
        updateCheckoutGroupItemPosition({
          team,
          csrfToken,
          item: { ...movedItem, position: destination.index + 1 }
        }).catch(failed('Failed to reorder item')).finally(() => setReordering(false));
      } else {
        const sourceItems = [...(sourceGroup.checkout_group_items || [])];
        sourceItems.splice(source.index, 1);
        const destItems = [...(destGroup.checkout_group_items || [])];
        const moved = { ...movedItem, checkout_group_id: destGroupId };
        destItems.splice(destination.index, 0, moved);

        const updated = checkoutCategories.map((c) => ({
          ...c,
          checkout_groups: (c.checkout_groups || []).map((g) => {
            if (g.id === sourceGroupId) return { ...g, checkout_group_items: sourceItems };
            if (g.id === destGroupId) return { ...g, checkout_group_items: destItems };
            return g;
          })
        }));
        setCategories(updated);
        moveCheckoutGroupItem({
          team,
          csrfToken,
          item: movedItem,
          newCheckoutGroupId: destGroupId,
          position: destination.index + 1
        }).catch(failed('Failed to move item')).finally(() => setReordering(false));
      }
    }
  };

  const canDeleteTab = checkoutCategories.length > 1;

  return (
    <div className="cwb-structure-tab d-flex flex-column" style={{ flex: 1, minHeight: 0 }}>
      <div className="cwb-section-label">Tabs</div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="tabs" type="tab">
          {(tabsProvided) => (
            <div ref={tabsProvided.innerRef} {...tabsProvided.droppableProps}>
              {checkoutCategories.map((tab, ti) => (
                <Draggable key={tab.id} draggableId={`tab-${tab.id}`} index={ti} isDragDisabled={reordering}>
                  {(tabProvided) => (
                    <div
                      ref={tabProvided.innerRef}
                      {...tabProvided.draggableProps}
                      className="cwb-structure-section"
                      style={tabProvided.draggableProps.style}>
                      <div className="cwb-tab-item">
                        <div className="cwb-tab-drag-handle" {...tabProvided.dragHandleProps}>
                          <DotsGrid width={14} height={14} aria-hidden="true" />
                        </div>
                        <div className="cwb-tab-icon">
                          <Folder width={16} height={16} aria-hidden="true" />
                        </div>
                        <div className="cwb-tab-name">
                          <RenameableName
                            value={tab.name}
                            onCommit={(name) => handleRenameTab(tab, name)}
                          />
                        </div>
                        <div className="cwb-tab-actions">
                          <Button
                            variant="tertiary"
                            size="sm"
                            icon={Copy01}
                            title="Duplicate tab"
                            aria-label="Duplicate tab"
                            onClick={() => handleDuplicateTab(tab)}
                          />
                          <Button
                            variant="tertiary"
                            size="sm"
                            icon={Trash01}
                            title={canDeleteTab ? 'Delete tab' : 'At least one tab is required'}
                            aria-label="Delete tab"
                            disabled={!canDeleteTab}
                            onClick={() => requestDeleteTab(tab)}
                          />
                        </div>
                      </div>

                      <div className="cwb-tab-children">
                        <Droppable droppableId={`tab-${tab.id}-groups`} type="group">
                          {(groupsProvided, groupsSnapshot) => (
                            <div
                              ref={groupsProvided.innerRef}
                              {...groupsProvided.droppableProps}
                              className={`cwb-tab-groups${groupsSnapshot.isDraggingOver ? ' is-dragging-over' : ''}`}>
                              {(tab.checkout_groups || []).map((group, gi) => (
                                <Draggable
                                  key={group.id}
                                  draggableId={`group-${group.id}`}
                                  index={gi}
                                  isDragDisabled={reordering}>
                                  {(groupProvided) => (
                                    <div
                                      ref={groupProvided.innerRef}
                                      {...groupProvided.draggableProps}
                                      style={groupProvided.draggableProps.style}>
                                      <div className="cwb-group-item">
                                        <div className="cwb-tab-drag-handle" {...groupProvided.dragHandleProps}>
                                          <DotsGrid width={14} height={14} aria-hidden="true" />
                                        </div>
                                        <div className="cwb-group-icon">
                                          <LayersThree01 width={14} height={14} aria-hidden="true" />
                                        </div>
                                        <div className="cwb-group-name">
                                          <RenameableName
                                            value={group.name}
                                            onCommit={(name) =>
                                              handleRenameGroup(tab.id, group, name)
                                            }
                                          />
                                        </div>
                                        <div className="cwb-group-actions">
                                          <Button
                                            variant="tertiary"
                                            size="sm"
                                            icon={Trash01}
                                            title="Delete group"
                                            aria-label="Delete group"
                                            onClick={() => requestDeleteGroup(tab, group)}
                                          />
                                        </div>
                                      </div>

                                      <div className="cwb-group-children">
                                        <Droppable
                                          droppableId={`group-${group.id}-items`}
                                          type="item">
                                          {(itemsProvided, itemsSnapshot) => (
                                            <div
                                              ref={itemsProvided.innerRef}
                                              {...itemsProvided.droppableProps}
                                              className={`cwb-group-items${itemsSnapshot.isDraggingOver ? ' is-dragging-over' : ''}`}>
                                              {(group.checkout_group_items || []).map((item, ii) => (
                                                <Draggable
                                                  key={item.id}
                                                  draggableId={`item-${item.id}`}
                                                  index={ii}
                                                  isDragDisabled={reordering}>
                                                  {(itemProvided) => (
                                                    <div
                                                      ref={itemProvided.innerRef}
                                                      {...itemProvided.draggableProps}
                                                      {...itemProvided.dragHandleProps}
                                                      className="cwb-ticket-item"
                                                      style={itemProvided.draggableProps.style}>
                                                      <div className={`cwb-ticket-type-dot ${itemDotClass(item)}`} />
                                                      <div className="cwb-ticket-label">{itemDisplayName(item)}</div>
                                                      {itemPrice(item) && (
                                                        <div className="cwb-ticket-price">{itemPrice(item)}</div>
                                                      )}
                                                      <Button
                                                        variant="tertiary"
                                                        size="sm"
                                                        icon={XClose}
                                                        className="cwb-ticket-item-delete"
                                                        title="Remove item"
                                                        aria-label="Remove item"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          requestDeleteItem(item);
                                                        }}
                                                      />
                                                    </div>
                                                  )}
                                                </Draggable>
                                              ))}
                                              {itemsProvided.placeholder}
                                            </div>
                                          )}
                                        </Droppable>

                                        <AddItemMenu
                                          onSelect={(payload) => handleSelectAddItem(group.id, payload)}
                                          confirm={confirm}
                                          team={team}
                                          ticketTypes={ticketTypes}
                                          addOns={addOns}
                                          memberships={memberships}
                                          seatingChartEvents={seatingChartEvents}
                                          canAddDonation={canAddDonation(confirm, checkoutCategories)}
                                          canAddSeatedAdmissionChart={canAddSeatedAdmissionChart(confirm, checkoutCategories)}
                                          canAddSeatedAddOnChart={canAddSeatedAddOnChart(confirm, checkoutCategories)}
                                        />
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {groupsProvided.placeholder}
                            </div>
                          )}
                        </Droppable>

                        <Button
                          variant="link-color"
                          size="sm"
                          icon={Plus}
                          style={{ marginTop: '4px' }}
                          onClick={() => handleAddGroup(tab)}>
                          Add group
                        </Button>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {tabsProvided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <Button
        variant="link-color"
        size="sm"
        icon={Plus}
        style={{ marginTop: '4px', alignSelf: 'flex-start' }}
        onClick={handleAddTab}>
        Add tab
      </Button>

      <div className="cwb-item-types-key">
        <div className="cwb-section-label">Item types</div>
        <div className="cwb-item-types-key-row">
          {LEGEND_ITEMS.map(({ dotClass, label }) => (
            <div key={dotClass} className="cwb-item-types-key-item">
              <div className={`cwb-ticket-type-dot ${dotClass}`} />
              <span>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {pendingDelete && (
        <DestructiveConfirmModal
          open
          onClose={() => setPendingDelete(null)}
          title={pendingDelete.title}
          body={pendingDelete.body}
          confirmLabel={pendingDelete.confirmLabel}
          onConfirm={pendingDelete.onConfirm}
        />
      )}
    </div>
  );
};

export default StructureTab;
